import React, { useState, useEffect } from "react";
import "./index.css";
import {
  Layout,
  Input,
  Button,
  Select,
  message,
  Spin,
  Radio,
  Form,
} from "antd";
import { get, post } from "../../../../utils/apiUtils";
import surveyGeneratorHeader from "../../../../assets/img/surveyGeneratorHeader.png";
import surveyGeneratorPhoto from "../../../../assets/img/aiGeneratorPhoto.png";
import SurveyPreview from "./SurveyPreview";
import { getUserRole } from "../../../../hooks/getUserRoles";
import { useGate } from "../../../Subscription/entitlements";
import { normalizeRole } from "../../../../utils/roles";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;

interface SurveyConfiguration {
  type: "both" | "quantitative" | "qualitative";
  length: "5-10" | "11-20" | "20+";
}

export type LanguageType = {
  id: string;
  name: string;
  code: string;
};

const AISurveyGenerator: React.FC = () => {
  const { t } = useTranslation("aiTools");
  const userRole = getUserRole();
  const gate = useGate();
  const [form] = Form.useForm();

  const [surveyConfiguration, setSurveyConfiguration] =
    useState<SurveyConfiguration>({
      type: "both",
      length: "5-10",
    });

  const [supportedLanguages, setSupportedLanguages] = useState<LanguageType[]>(
    []
  );

  // për preview (languageId)
  const [languageId, setLanguageId] = useState("");

  // UI only (për active class)
  const [selectedType, setSelectedType] = useState("");
  const [selectedLength, setSelectedLength] = useState("");

  const [surveyQuestions, setSurveyQuestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [completeSurvey, setCompleteSurvey] = useState<any[]>([]);

  const [surveyDetails, setSurveyDetails] = useState({
    description: "",
    language: "",
    surveyType: "both",
    surveyLength: "5-10",
    generated: false,
  });

  const transformSurveyData = (apiData: any) => {
    return (apiData || []).map((item: any) => {
      let question_type: string;
      let options: any[] = [];

      switch (item.question_type) {
        case "Select One":
          question_type = "singleSelect";
          options = item.choice_label_texts || [];
          break;
        case "Select Multiple":
          question_type = "multiSelect";
          options = item.choice_label_texts || [];
          break;
        case "Number":
          question_type = "number";
          break;
        case "Date & Time":
          question_type = "dateTime";
          break;
        default:
          question_type = "text";
      }

      return {
        id: item.question_id,
        question: item.question_label_text,
        answerType: question_type,
        options,
        textAnswer: "",
        singleSelectAnswer: "",
        multipleSelectAnswers: [],
        numberAnswer: null,
        dateAnswer: "",
        timeAnswer: "",
        dateTimeAnswer: "",
        uploadedFiles: [],
        phoneNumberAnswer: "",
      };
    });
  };

  useEffect(() => {
    const fetchSupportedLanguages = async () => {
      try {
        const response = await get("languages/", {});
        setSupportedLanguages(response.data as LanguageType[]);
      } catch (error) {
        console.error("Error fetching supported languages:", error);
      }
    };

    fetchSupportedLanguages();
  }, []);

  const handleSurveyDetailChange = (_changed: any, allValues: any) => {
    setSurveyDetails(allValues);
  };

  const handleLanguageChange = (code: string) => {
    // ruaj languageId per preview
    const selected = supportedLanguages.find((l) => l.code === code);
    if (selected) setLanguageId(selected.id);

    // ruaj edhe për active UI nëse do
    // form value rregullohet automatikisht prej Form.Item
  };

  const handleTypeChange = (e: any) => {
    setSelectedType(e.target.value);
    // sigurohu qe Form value perditesohet
    form.setFieldsValue({ surveyType: e.target.value });
  };

  const handleLengthChange = (e: any) => {
    setSelectedLength(e.target.value);
    form.setFieldsValue({ surveyLength: e.target.value });
  };

  const handleGenerateSurvey = async (values: any) => {
    const surveyLengthMapping: Record<string, string> = {
      "5-10": "10",
      "11-20": "20",
      "20+": "20",
    };

    const apiTypeMap: Record<string, string> = {
      qualitative: "Qualitative",
      quantitative: "Quantitative",
      both: "Both",
    };

    setIsLoading(true);

    try {
      const fullLanguageName = supportedLanguages.find(
        (lang) => lang.code === values.language
      )?.name;

      if (!fullLanguageName) {
        message.error(t("surveyGeneratorForm.messages.languageNotFound"));
        return;
      }

      const maxLength =
        surveyLengthMapping[values.surveyLength] || values.surveyLength;

      const apiType = apiTypeMap[values.surveyType];
      if (!apiType) {
        message.error(t("surveyGeneratorForm.messages.invalidType"));
        return;
      }

      // ✅ IDENTIK si swagger (S e madhe, underscore, slash para ?)
      const url =
        `/Survey_generator/?language=${encodeURIComponent(fullLanguageName)}` +
        `&Type=${encodeURIComponent(apiType)}` +
        `&length=${encodeURIComponent(maxLength)}`;

      console.log("Survey generator URL:", url);
      console.log("Survey generator body:", {
        description: values.description,
      });

      const response = await post<any>(
        url,
        { description: values.description },
        { headers: { "Content-Type": "application/json" } }
      );

      // setCompleteSurvey(response?.data);
      // setSurveyQuestions(transformSurveyData(response?.data));
      setCompleteSurvey(response?.data);

      const questionsArray = response?.data?.questions ?? [];
      const transformedData = transformSurveyData(questionsArray);

      setSurveyQuestions(transformedData);

      setShowPreview(true);

      setSurveyConfiguration({
        type: values.surveyType,
        length: values.surveyLength,
      });

      gate.refresh();
    } catch (error: any) {
      console.error("Error fetching survey data:", error);
      console.log(
        "BadRequest debug:",
        error?.response?.status,
        error?.response?.data
      );

      const backendMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;

      message.error(
        backendMsg || t("surveyGeneratorForm.messages.fetchError")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!showPreview ? (
        <div className="layout-style" style={{ marginLeft: 0 }}>
          <Content
            className="content-style"
            style={{ padding: "0 24", margin: 0, minHeight: 280 }}
          >
            <div className="header-image">
              <img
                src={surveyGeneratorHeader}
                alt="AI Survey Generator"
                style={{ width: "100%" }}
              />
            </div>

            <Form
              form={form}
              className="survey-form"
              layout="vertical"
              onFinish={(values) =>
                gate.requireTokens(0, () => handleGenerateSurvey(values))
              }
              onValuesChange={handleSurveyDetailChange}
              initialValues={surveyDetails}
            >
              <div className="card">
                <div className="survey-form-first-content">
                  <Form.Item
                    label={t("surveyGeneratorForm.selectLanguageLabel")}
                    required
                    name="language"
                    className="label-generate-survey"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "surveyGeneratorForm.selectLanguageRequired"
                        ),
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder={t(
                        "surveyGeneratorForm.selectLanguagePlaceholder"
                      )}
                      className="select-language-drowpdown-style"
                      // ❗MOS e kontrollo me value={languageId} / language
                      onChange={handleLanguageChange}
                      filterOption={(input, option: any) =>
                        option?.children
                          ?.toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {supportedLanguages?.map((lang) => (
                        <Option key={lang.id} value={lang.code}>
                          {lang.name} - {lang.code}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <div className="survey-form-second-content">
                    <Form.Item
                      name="surveyType"
                      label={t("surveyGeneratorForm.selectTypeLabel")}
                      required
                      className="label"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "surveyGeneratorForm.selectTypeRequired"
                          ),
                        },
                      ]}
                    >
                      <Radio.Group
                        className="custom-radio-group"
                        value={selectedType || form.getFieldValue("surveyType")}
                        onChange={handleTypeChange}
                      >
                        <div
                          className={`radio-divv ${
                            selectedType === "quantitative" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="quantitative">
                            {t("surveyGeneratorForm.typeQuantitative")}
                          </Radio>
                        </div>

                        <div
                          className={`radio-divv ${
                            selectedType === "qualitative" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="qualitative">
                            {t("surveyGeneratorForm.typeQualitative")}
                          </Radio>
                        </div>

                        <div
                          className={`radio-divv ${
                            selectedType === "both" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="both">
                            {t("surveyGeneratorForm.typeBoth")}
                          </Radio>
                        </div>
                      </Radio.Group>
                    </Form.Item>

                    <Form.Item
                      name="surveyLength"
                      label={t("surveyGeneratorForm.selectLengthLabel")}
                      required
                      className="label"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "surveyGeneratorForm.selectLengthRequired"
                          ),
                        },
                      ]}
                    >
                      <Radio.Group
                        className="custom-radio-group"
                        value={
                          selectedLength || form.getFieldValue("surveyLength")
                        }
                        onChange={handleLengthChange}
                      >
                        <div
                          className={`radio-second-div ${
                            selectedLength === "5-10" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="5-10">
                            <span>5-10</span>
                            <br />
                            {t("surveyGeneratorForm.questionsSuffix")}
                          </Radio>
                        </div>

                        <div
                          className={`radio-second-div ${
                            selectedLength === "11-20" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="11-20">
                            <span>11-20</span>
                            <br />
                            {t("surveyGeneratorForm.questionsSuffix")}
                          </Radio>
                        </div>

                        <div
                          className={`radio-second-div ${
                            selectedLength === "20+" ? "active" : ""
                          }`}
                        >
                          <Radio className="custom-radio" value="20+">
                            <span>20+</span>
                            <br />
                            {t("surveyGeneratorForm.questionsSuffix")}
                          </Radio>
                        </div>
                      </Radio.Group>
                    </Form.Item>
                  </div>

                  <div className="first-inner-section">
                    <div className="first-inner-content-survey-generator">
                      <img
                        src={surveyGeneratorPhoto}
                        alt="survey generator"
                        className="surveyGeneratorImg"
                      />
                      <div className="discribe-your-survey-section-g">
                        <Form.Item
                          label={t("surveyGeneratorForm.describeLabel")}
                          required
                          name="description"
                          className="label describe-your-survey-label"
                          rules={[
                            {
                              required: true,
                              message: t(
                                "surveyGeneratorForm.describeRequired"
                              ),
                            },
                          ]}
                        >
                          <TextArea
                            placeholder={t(
                              "surveyGeneratorForm.describePlaceholder"
                            )}
                            className="describe-your-survey-textarea-style"
                            autoSize={{ minRows: 3, maxRows: 20 }}
                          />
                        </Form.Item>

                        <Form.Item>
                          <Button
                            disabled={normalizeRole(userRole) === "viewer"}
                            htmlType="submit"
                            className="generate-survey-button"
                          >
                            {isLoading ? (
                              <Spin />
                            ) : (
                              t("surveyGeneratorForm.generateButton")
                            )}
                          </Button>
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </Content>
        </div>
      ) : (
        <SurveyPreview
          config={surveyConfiguration}
          surveyQuestions={surveyQuestions}
          completeSurvey={completeSurvey}
          languageId={languageId}
        />
      )}
    </>
  );
};

export default AISurveyGenerator;
